import { Button } from "@/components/ui/button";
import {
  InAppNotification,
  NotificationType,
  useMarkNotificationAsRead,
} from "@/lib/api/notifications";
import { useMentorView } from "@/lib/hooks/admin/use-mentor-view";
import { useCurrentConversation } from "@/lib/hooks/conversations/use-current-conversation";
import { cn } from "@/lib/utils";
import {
  AcademicCapIcon,
  CalendarIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  FlagIcon,
  StarIcon,
  UserIcon,
  VideoCameraIcon,
  CameraIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/solid";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { BrandIcon } from "@somethings-ui/web";
import { useRouter, useRouterState } from "@tanstack/react-router";
import { formatDistanceToNow } from "date-fns";
import { replaceTimePlaceholders } from "@/lib/utils/notification";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";

const STORY_EDITOR_LABEL: Partial<Record<NotificationType, string>> = {
  [NotificationType.MENTOR_STORY_REJECTED]: "First Moments",
  [NotificationType.MENTOR_STORY_APPROVED]: "View story",
  [NotificationType.MENTOR_STORY_REMOVED]: "Go to Story Editor",
};

const SHAREABLE_ENTITY_CONFIG: Record<
  string,
  {
    label: string;
    bgColor: string;
    iconColor: string;
    icon: React.ComponentType<{ className?: string }>;
  }
> = {
  note: {
    label: "View Note",
    bgColor: "bg-orange-100",
    iconColor: "text-orange-brand",
    icon: DocumentTextIcon,
  },
  moment: {
    label: "View",
    bgColor: "bg-blue-100",
    iconColor: "text-blue-brand",
    icon: CameraIcon,
  },
  swap: {
    label: "View Swap",
    bgColor: "bg-purple-100",
    iconColor: "text-purple-600",
    icon: ArrowPathIcon,
  },
};

const getShareableEntityConfig = (entityType?: string) => {
  const key = entityType?.toLowerCase() ?? "";
  return SHAREABLE_ENTITY_CONFIG[key] ?? SHAREABLE_ENTITY_CONFIG.note;
};

const getShareableEntityLabel = (entityType?: string) => {
  return getShareableEntityConfig(entityType).label;
};

interface NotificationItemProps {
  notification: InAppNotification;
  timezone: string;
  className?: string;
  isAdmin?: boolean;
  setClose?: () => void;
}

const NotificationActions = ({
  notification,
  isAdmin,
  setClose,
}: {
  notification: InAppNotification;
  isAdmin?: boolean;
  setClose?: () => void;
}) => {
  const { mentorView, setMentorView } = useMentorView();
  const routerState = useRouterState();
  const router = useRouter();
  const { mutateAsync: markAsRead } = useMarkNotificationAsRead();
  const { setCurrentConversation } = useCurrentConversation();
  const { type, data } = notification;
  const { track } = useTrackEvent();

  const trackTodoClicked = () => {
    track(PostHogEvents.TODO_CLICKED, {
      notification_type: notification.type,
      notification_id: notification.id,
      teen_name: notification.data?.sender?.name ?? notification.data?.menteeName,
    });
  };

  const markAsReadAction = {
    label: "Mark as Read",
    onClick: () => {
      trackTodoClicked();
      markAsRead(notification.id);
    },
  };

  const dismissAction = notification.read_at
    ? undefined // If the notification is already read, don't show the dismiss action
    : {
        label: "Dismiss",
        onClick: () => {
          trackTodoClicked();
          markAsRead(notification.id);
        },
      };

  let primaryAction:
    | {
        label: string;
        onClick: () => void;
        variant?: "dark" | "light";
      }
    | undefined;

  let secondaryAction:
    | {
        label: string;
        onClick: () => void;
      }
    | undefined;

  const handleNotificationClick = () => {
    trackTodoClicked();

    if (notification.type === NotificationType.MENTOR_MONTHLY_UPDATE) {
      router.navigate({
        to: `/mentor-monthly-updates/${notification.data?.mentorshipId}`,
      });
      setClose?.();
    } else {
      if (routerState.location.pathname !== "/") {
        router.navigate({
          to: "/conversations",
        });
      }
      if (!mentorView) {
        setMentorView(true);
      }
      if (notification.data?.sdkUrl) {
        setCurrentConversation(notification.data?.sdkUrl as string);
      }
    }

    // If the notification is not read, mark it as read
    if (
      !notification.read_at &&
      notification.type !== NotificationType.WHAT_TO_WORK_ON
    ) {
      markAsRead(notification.id);
    }
  };

  const handleUserReportClick = () => {
    trackTodoClicked();
    router.navigate({
      to: "/risk-flagging",
      search: { reportId: notification.data?.reportId },
    });
    if (!notification.read_at) {
      markAsRead(notification.id);
    }
    setClose?.();
  };

  const handleViewShareableEntity = () => {
    trackTodoClicked();

    const entityId = data?.entityId ?? data?.noteId;
    if (!entityId) {
      console.warn("No entityId found in notification data", data);
      return;
    }

    const entityType = (data?.sharedEntityType ?? "note").toLowerCase();
    const channel =
      typeof data?.sdkUrl === "string" ? data.sdkUrl : undefined;

    if (!mentorView) {
      setMentorView(true);
    }

    if (channel) {
      setCurrentConversation(channel);
      useCurrentConversation.getState().setPendingEntity({
        id: entityId,
        type: entityType.toUpperCase(),
        channel,
      });
    }

    router.navigate({
      to: "/conversations",
      search: {
        segment: "teens",
        ...(channel ? { channel } : {}),
      },
    });

    if (!notification.read_at) {
      markAsRead(notification.id);
    }
    setClose?.();
  };

  const handleViewNoteReminder = () => {
    trackTodoClicked();

    if (!mentorView) {
      setMentorView(true);
    }

    if (data?.sdkUrl) {
      setCurrentConversation(data.sdkUrl as string);
      useCurrentConversation.getState().setPendingResourcesTab("notes");
    }

    router.navigate({
      to: "/conversations",
      search: {
        segment: "teens",
        ...(data?.sdkUrl ? { channel: data.sdkUrl as string } : {}),
      },
    });

    if (!notification.read_at) {
      markAsRead(notification.id);
    }
    setClose?.();
  };

  const handleReviewSimulator = () => {
    trackTodoClicked();

    router.navigate({ to: "/training" });

    if (!notification.read_at) {
      markAsRead(notification.id);
    }
    setClose?.();
  };

  const handleReviewPresentation = () => {
    trackTodoClicked();

    const menteeId = notification.data?.menteeId;
    if (menteeId) {
      router.navigate({
        to: "/users/$userId",
        params: { userId: menteeId },
        search: { tab: "care" },
      });
    } else {
      router.navigate({ to: "/users" });
    }

    if (!notification.read_at) {
      markAsRead(notification.id);
    }
    setClose?.();
  };

  const handleOpenStory = () => {
    trackTodoClicked();
    router.navigate({ to: "/profile/first-moments" });
    if (!notification.read_at) markAsRead(notification.id);
    setClose?.();
  };

  const openReview = (
    to:
      | "/users/mentor-profile-reviews/$reviewId"
      | "/users/mentor-story-reviews/$reviewId",
  ) => {
    trackTodoClicked();
    const reviewId = notification.data?.reviewId;
    if (reviewId) {
      router.navigate({ to, params: { reviewId } });
    } else {
      router.navigate({ to: "/users" });
    }
    if (!notification.read_at) markAsRead(notification.id);
    setClose?.();
  };

  switch (type) {
    case NotificationType.WHAT_TO_WORK_ON:
      primaryAction = {
        label: "Go to conversation",
        onClick: handleNotificationClick,
      };
      secondaryAction = undefined;
      break;
    case NotificationType.NEW_TEEN:
      primaryAction = {
        label: "Meet Your New Teen",
        onClick: handleNotificationClick,
      };
      break;
    case NotificationType.ESCALATION:
      primaryAction = {
        label: "View Escalation Note",
        onClick: handleNotificationClick,
      };
      secondaryAction = dismissAction;
      break;
    case NotificationType.NOTE_SHARED:
      primaryAction = {
        label: "View Note",
        onClick: handleNotificationClick,
      };
      secondaryAction = dismissAction;
      break;
    case NotificationType.SESSION:
      primaryAction = markAsReadAction;
      break;
    case NotificationType.REMINDER:
      primaryAction = markAsReadAction;
      break;

    case NotificationType.TEEN_DEACTIVATED:
      primaryAction = markAsReadAction;
      break;
    case NotificationType.MENTOR_MONTHLY_UPDATE:
      primaryAction = {
        label: "Record Monthly Update",
        onClick: handleNotificationClick,
      };
      break;
    case NotificationType.SHAREABLE_ENTITY_SHARED: {
      const isSwapPending = data?.sharedEntityType?.toLowerCase() === "swap" && data?.pending_other_user;
      primaryAction = {
        label: isSwapPending ? "Add Your Photo" : getShareableEntityLabel(data?.sharedEntityType),
        onClick: handleViewShareableEntity,
        variant: "dark"
      };
      secondaryAction = dismissAction;
      break;
    }
    case NotificationType.SWAP_ACTION_REQUIRED: {
      primaryAction = {
        label: "Add Your Photo",
        onClick: handleViewShareableEntity,
        variant: "dark"
      };
      secondaryAction = dismissAction;
      break;
    }
    case NotificationType.USER_REPORT:
      primaryAction = {
        label: "Review Report",
        onClick: handleUserReportClick,
      };
      secondaryAction = dismissAction;
      break;
    case NotificationType.NOTE_REMINDER:
      primaryAction = {
        label: "View Note",
        onClick: handleViewNoteReminder,
      };
      secondaryAction = dismissAction;
      break;
    case NotificationType.TRAINING_SIMULATOR_COMPLETED:
      primaryAction = {
        label: "Review Simulator",
        onClick: handleReviewSimulator,
      };
      secondaryAction = dismissAction;
      break;
    case NotificationType.MENTOR_PROFILE_SUBMITTED:
      primaryAction = {
        label: "Review Match Profile",
        onClick: () => openReview("/users/mentor-profile-reviews/$reviewId"),
      };
      secondaryAction = dismissAction;
      break;
    case NotificationType.MENTOR_STORY_SUBMITTED:
      primaryAction = {
        label: "Review Story",
        onClick: () => openReview("/users/mentor-story-reviews/$reviewId"),
      };
      secondaryAction = dismissAction;
      break;
    case NotificationType.MENTOR_TIME_OFF_SCHEDULED:
      primaryAction = {
        label: "See Time Off",
        onClick: () => {
          trackTodoClicked();
          const mentorId = notification.data?.mentorId;
          if (mentorId) {
            router.navigate({
              to: "/users/$userId",
              params: { userId: mentorId },
              search: { tab: "schedule" },
            });
          } else {
            router.navigate({ to: "/users" });
          }
          if (!notification.read_at) markAsRead(notification.id);
          setClose?.();
        },
      };
      secondaryAction = dismissAction;
      break;
    case NotificationType.MENTOR_STORY_REJECTED:
    case NotificationType.MENTOR_STORY_APPROVED:
    case NotificationType.MENTOR_STORY_REMOVED:
      primaryAction = {
        label: STORY_EDITOR_LABEL[notification.type] ?? "First Moments",
        onClick: handleOpenStory,
      };
      secondaryAction = dismissAction;
      break;
    case NotificationType.CARE_REVIEW_DUE:
    case NotificationType.CARE_REVIEW_OVERDUE:
      primaryAction = {
        label: "Review Presentation",
        onClick: handleReviewPresentation,
      };
      secondaryAction = dismissAction;
      break;
    default:
      primaryAction = markAsReadAction;
      break;
  }

  if (isAdmin) {
    return null;
  }

  if (!primaryAction && !secondaryAction) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 pt-2">
      <div className="flex items-center gap-2 pt-2">
        {primaryAction && (
          <Button
            onClick={primaryAction.onClick}
            variant="outline"
            className={cn("w-full", primaryAction.variant === "dark" ? "bg-black text-white" : "")}
          >
            {primaryAction.label}
          </Button>
        )}
        {secondaryAction && (
          <Button variant="ghost" onClick={secondaryAction.onClick}>
            {secondaryAction.label}
          </Button>
        )}
      </div>
    </div>
  );
};

const NotificationIcon = ({ type, entityType }: { type: NotificationType; entityType?: string }) => {
  switch (type) {
    case NotificationType.NEW_TEEN:
      return (
        <BrandIcon
          bgColor="bg-blue-100"
          icon={<UserIcon className="text-blue-brand" />}
          size="sm"
        />
      );
    case NotificationType.NOTE_SHARED:
      return (
        <BrandIcon
          bgColor="bg-orange-100"
          icon={<StarIcon className="text-orange-brand" />}
          size="sm"
        />
      );
    case NotificationType.NOTE_REMINDER:
      return (
        <BrandIcon
          bgColor="bg-orange-100"
          icon={<DocumentTextIcon className="text-orange-brand" />}
          size="sm"
        />
      );
    case NotificationType.SESSION:
      return (
        <BrandIcon
          bgColor="bg-pink-100"
          icon={<CalendarIcon className="text-pink-brand" />}
          size="sm"
        />
      );
    case NotificationType.TEEN_DEACTIVATED:
      return (
        <BrandIcon
          bgColor="bg-error-100"
          icon={<FlagIcon className="text-error-brand" />}
          size="sm"
        />
      );
    case NotificationType.ESCALATION:
    case NotificationType.NEW_ESCALATION:
      return (
        <BrandIcon
          bgColor="bg-error-100"
          size="sm"
          icon={<ExclamationTriangleIcon className="text-error-brand" />}
        />
      );
    case NotificationType.MENTOR_MONTHLY_UPDATE:
      return (
        <BrandIcon
          bgColor="bg-blue-100"
          size="sm"
          icon={<VideoCameraIcon className="text-blue-brand" />}
        />
      );
    case NotificationType.WHAT_TO_WORK_ON:
      return (
        <BrandIcon
          bgColor="bg-green-100"
          size="sm"
          icon={<CheckCircleIcon className="text-green-600" />}
        />
      );
    case NotificationType.USER_REPORT:
      return (
        <BrandIcon
          bgColor="bg-error-100"
          size="sm"
          icon={<FlagIcon className="text-error-brand" />}
        />
      );
    case NotificationType.SHAREABLE_ENTITY_SHARED: {
      const config = getShareableEntityConfig(entityType);
      const Icon = config.icon;
      return (
        <BrandIcon
          bgColor={config.bgColor}
          size="sm"
          icon={<Icon className={config.iconColor} />}
        />
      );
    }
    case NotificationType.SWAP_ACTION_REQUIRED: {
      const config = getShareableEntityConfig("swap");
      const Icon = config.icon;
      return (
        <BrandIcon
          bgColor={config.bgColor}
          size="sm"
          icon={<Icon className={config.iconColor} />}
        />
      );
    }
    case NotificationType.TRAINING_SIMULATOR_COMPLETED:
      return (
        <BrandIcon
          bgColor="bg-green-100"
          size="sm"
          icon={<AcademicCapIcon className="text-green-600" />}
        />
      );
    case NotificationType.MENTOR_PROFILE_SUBMITTED:
    case NotificationType.MENTOR_STORY_SUBMITTED:
    case NotificationType.MENTOR_STORY_REJECTED:
    case NotificationType.MENTOR_STORY_APPROVED:
    case NotificationType.MENTOR_STORY_REMOVED:
      return (
        <BrandIcon
          bgColor="bg-blue-100"
          size="sm"
          icon={<UserIcon className="text-blue-brand" />}
        />
      );
    case NotificationType.CARE_REVIEW_DUE:
      return (
        <BrandIcon
          bgColor="bg-blue-100"
          size="sm"
          icon={<ClipboardDocumentCheckIcon className="text-blue-brand" />}
        />
      );
    case NotificationType.CARE_REVIEW_OVERDUE:
      return (
        <BrandIcon
          bgColor="bg-error-100"
          size="sm"
          icon={<ExclamationCircleIcon className="text-error-brand" />}
        />
      );
  }
};

export function NotificationItem({
  notification,
  timezone,
  className,
  isAdmin,
  setClose,
}: NotificationItemProps) {
  const { title, content, created_at, type, data } = notification;

  return (
    <div
      className={cn(
        "hover:bg-accent/5 border-b p-4 transition-colors",
        className,
      )}
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 space-y-1">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-tertiary">
                {data?.sender?.name ?? data?.ownerName ?? data?.menteeName}
              </span>
            </div>
            <span className="text-muted-foreground text-xs">
              {formatDistanceToNow(new Date(created_at), { addSuffix: true })}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <NotificationIcon type={type} entityType={data?.sharedEntityType} />
            <h4 className="text-sm font-bold">
              {type === NotificationType.CARE_REVIEW_DUE ? `${title}:` : title}
            </h4>
          </div>

          {/* Content */}
          <p className="text-muted-foreground text-sm">
            {replaceTimePlaceholders(content, { timezone })}
          </p>

          <NotificationActions
            notification={notification}
            isAdmin={isAdmin}
            setClose={setClose}
          />
        </div>
      </div>
    </div>
  );
}
